import { Link } from 'react-router-dom';
import { ArrowRight, Check, Award } from 'lucide-react';
import Breadcrumbs from '../components/shared/Breadcrumbs';
import AffiliateBanner from '../components/shared/AffiliateBanner';
import CTABox from '../components/shared/CTABox';
import TableOfContents from '../components/shared/TableOfContents';
import StarRating from '../components/shared/StarRating';
import ProsConsList from '../components/shared/ProsConsList';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const NorthwestReviewPage = () => {
  const tocItems = [
    { id: 'einleitung', title: 'Einleitung', level: 2 },
    { id: 'unternehmensprofil', title: 'Unternehmensprofil', level: 2 },
    { id: 'leistungen', title: 'Was bietet Northwest?', level: 2 },
    { id: 'preise', title: 'Preise und Pakete', level: 2 },
    { id: 'praxistest', title: 'Unser Praxistest', level: 2 },
    { id: 'datenschutz', title: 'Datenschutz', level: 2 },
    { id: 'kundenservice', title: 'Kundenservice', level: 2 },
    { id: 'dashboard', title: 'Dashboard & Verwaltung', level: 2 },
    { id: 'pros-cons', title: 'Vorteile & Nachteile', level: 2 },
    { id: 'vergleich', title: 'Northwest vs. Alternativen', level: 2 },
    { id: 'zielgruppe', title: 'Für wen geeignet?', level: 2 },
    { id: 'fazit', title: 'Unser Fazit', level: 2 },
  ];

  const competitorComparison = [
    { 
      name: 'Northwest Registered Agent', 
      rating: 4.9, 
      price: '$39 + Staat', 
      raIncluded: '1 Jahr kostenlos',
      privacy: 'Sehr hoch',
      support: 'US-basiert',
      isWinner: true 
    },
    { 
      name: 'ZenBusiness', 
      rating: 4.1, 
      price: '$0 + Staat', 
      raIncluded: '$199/Jahr extra',
      privacy: 'Mittel',
      support: 'Chat/Email',
      isWinner: false 
    },
    { 
      name: 'Incfile', 
      rating: 3.8, 
      price: '$0 + Staat', 
      raIncluded: '$119/Jahr extra',
      privacy: 'Niedrig',
      support: 'Email',
      isWinner: false 
    },
    { 
      name: 'LegalZoom', 
      rating: 3.5, 
      price: '$79 + Staat', 
      raIncluded: '$299/Jahr extra',
      privacy: 'Mittel',
      support: 'Telefon',
      isWinner: false 
    },
  ];

  const faqs = [
    {
      question: 'Ist Northwest Registered Agent seriös?',
      answer: 'Ja, Northwest ist ein etabliertes Unternehmen mit über 25 Jahren Erfahrung (gegründet 1998) und mehr als 3 Millionen betreuten Kunden. Das Unternehmen hat seinen Hauptsitz in Spokane, Washington, und bietet Services in allen 50 US-Bundesstaaten an.'
    },
    {
      question: 'Wie schnell wird meine LLC gegründet?',
      answer: 'Die Bearbeitungszeit hängt vom gewählten Bundesstaat ab. Wyoming beispielsweise bearbeitet Anträge oft innerhalb von 1-2 Werktagen. Northwest reicht Ihren Antrag am selben oder nächsten Werktag ein.'
    },
    {
      question: 'Kann ich Northwest auch als Deutscher nutzen?',
      answer: 'Absolut. Northwest hat extensive Erfahrung mit internationalen Kunden. Sie können die gesamte Gründung online durchführen, ohne in die USA reisen zu müssen.'
    },
    {
      question: 'Was ist der Unterschied zwischen $39 und anderen Paketen?',
      answer: 'Das $39-Basispaket enthält alles, was Sie für eine rechtsgültige LLC-Gründung benötigen: Einreichung der Articles of Organization, 1 Jahr Registered Agent Service, und das Operating Agreement. Zusatzleistungen wie EIN-Beschaffung können separat hinzugefügt werden.'
    },
    {
      question: 'Kann ich meinen Registered Agent später zu Northwest wechseln?',
      answer: 'Ja, der Wechsel ist jederzeit möglich. Northwest übernimmt die notwendigen Formalitäten und benachrichtigt den alten Anbieter sowie den Bundesstaat.'
    },
    {
      question: 'Bietet Northwest auch Buchhaltung oder Steuerberatung?',
      answer: 'Northwest konzentriert sich auf LLC-Gründung und Registered Agent Services. Für Buchhaltung und Steuerberatung empfehlen sie spezialisierte Partner, was wir als Stärke sehen — sie übernehmen sich nicht mit Bereichen außerhalb ihrer Expertise.'
    }
  ];

  return (
    <div className="py-12" data-testid="northwest-review-page">
      <div className="container-wide">
        <Breadcrumbs items={[
          { label: 'Registered Agent', href: '/registered-agent' },
          { label: 'Northwest Registered Agent Testbericht', href: '/northwest-registered-agent-erfahrungen' }
        ]} />
        
        <AffiliateBanner />

        <div className="grid lg:grid-cols-12 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-8">
            <article className="prose-reviewlex">
              {/* Hero */}
              <header className="mb-12">
                <div className="flex items-center gap-3 mb-4">
                  <span className="badge-teal flex items-center gap-1">
                    <Award className="w-3 h-3" />
                    Testsieger 2026
                  </span>
                </div>
                <h1 className="mb-6">Northwest Registered Agent — Ausführlicher Testbericht 2026</h1>
                <div className="flex items-center gap-4 mb-6">
                  <StarRating rating={4.9} size="lg" />
                  <span className="text-slate-500">|</span>
                  <span className="text-slate-600">Unser #1 Empfohlener LLC-Service</span>
                </div>
                <p className="text-xl text-slate-600 mb-6">
                  Wir nutzen Northwest Registered Agent seit über 2 Jahren für unsere eigenen LLCs. 
                  In diesem ausführlichen Testbericht teilen wir unsere ehrlichen Erfahrungen — 
                  sowohl die Stärken als auch die wenigen Schwächen.
                </p>
                <div className="flex items-center gap-4 text-sm text-slate-500">
                  <span>Aktualisiert: April 2026</span>
                  <span>•</span>
                  <span>Lesezeit: 12 Min.</span>
                </div>
              </header>

              {/* Quick Summary Box */}
              <div className="card-highlight p-6 mb-12">
                <h4 className="font-heading font-bold text-navy-900 mb-4">Zusammenfassung</h4>
                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Gesamtbewertung</p>
                    <StarRating rating={4.9} size="md" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Startpreis</p>
                    <p className="font-bold text-navy-900">$39 + Staatsgebühren</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Registered Agent</p>
                    <p className="font-bold text-navy-900">$125/Jahr (1. Jahr kostenlos)</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Am besten für</p>
                    <p className="font-bold text-navy-900">Internationale Gründer</p>
                  </div>
                </div>
                <a
                  href="https://www.awin1.com/cread.php?awinmid=66946&awinaffid=1553052&ued=https%3A%2F%2Fwww.northwestregisteredagent.com%2Fservice-signup"
                  target="_blank"
                  rel="nofollow sponsored noopener noreferrer"
                  className="cta-primary w-full sm:w-auto"
                  data-testid="summary-cta"
                >
                  Northwest Registered Agent besuchen
                  <ArrowRight className="w-4 h-4 ml-2" />
                </a>
              </div>

              {/* Einleitung */}
              <section id="einleitung" className="mb-16">
                <h2>Einleitung</h2>
                <p>
                  Bei der Recherche für unsere eigene LLC-Gründung vor über zwei Jahren haben wir 
                  dutzende Anbieter verglichen. Northwest Registered Agent fiel dabei durch seine 
                  ungewöhnliche Kombination aus günstigem Preis, echtem Datenschutz und kompetenten 
                  Support positiv auf.
                </p>
                <p>
                  Seitdem haben wir nicht nur unsere eigenen LLCs über Northwest gegründet, sondern 
                  auch den Service im Alltag genutzt. Dieser Testbericht basiert auf echten Erfahrungen — 
                  nicht auf Herstellerangaben oder oberflächlichen Recherchen.
                </p>
              </section>

              {/* Unternehmensprofil */}
              <section id="unternehmensprofil" className="mb-16">
                <h2>Unternehmensprofil</h2>
                <p>
                  Northwest Registered Agent wurde 1998 in Spokane, Washington gegründet und ist damit 
                  einer der erfahrensten Anbieter im Markt. Das Unternehmen hat sich von einem lokalen 
                  Registered Agent Service zu einem vollständigen LLC-Gründungsanbieter entwickelt, 
                  der in allen 50 US-Bundesstaaten aktiv ist.
                </p>
                
                <div className="grid sm:grid-cols-2 gap-6 my-8">
                  <div className="card-flat p-4">
                    <p className="text-sm text-slate-500">Gegründet</p>
                    <p className="font-bold text-navy-900 text-lg">1998</p>
                  </div>
                  <div className="card-flat p-4">
                    <p className="text-sm text-slate-500">Hauptsitz</p>
                    <p className="font-bold text-navy-900 text-lg">Spokane, WA</p>
                  </div>
                  <div className="card-flat p-4">
                    <p className="text-sm text-slate-500">Betreute Kunden</p>
                    <p className="font-bold text-navy-900 text-lg">3+ Millionen</p>
                  </div>
                  <div className="card-flat p-4">
                    <p className="text-sm text-slate-500">Verfügbar in</p>
                    <p className="font-bold text-navy-900 text-lg">Alle 50 US-Staaten</p>
                  </div>
                </div>

                <p>
                  Anders als viele Konkurrenten, die versuchen, möglichst viele Zusatzservices zu 
                  verkaufen (Buchhaltung, Steuerberatung, Webseiten), konzentriert sich Northwest auf 
                  sein Kerngeschäft: LLC-Gründung und Registered Agent Services. Diese Fokussierung 
                  spiegelt sich in der Qualität wider.
                </p>
              </section>

              {/* Leistungen */}
              <section id="leistungen" className="mb-16">
                <h2>Was bietet Northwest?</h2>
                
                <div className="space-y-4">
                  <div className="card-flat p-6">
                    <h4 className="font-heading font-bold text-navy-900 mb-2">LLC/Corporation Gründung</h4>
                    <p className="text-slate-600 text-sm mb-0">
                      Komplette Gründung Ihrer LLC oder Corporation in jedem US-Bundesstaat. 
                      Northwest übernimmt die Namensprüfung, Dokumentenerstellung und Einreichung 
                      beim Secretary of State.
                    </p>
                  </div>
                  
                  <div className="card-flat p-6">
                    <h4 className="font-heading font-bold text-navy-900 mb-2">Registered Agent Service</h4>
                    <p className="text-slate-600 text-sm mb-0">
                      Professioneller Registered Agent in allen 50 Staaten. Northwest empfängt 
                      offizielle Dokumente für Ihre LLC und leitet sie digital an Sie weiter.
                    </p>
                  </div>
                  
                  <div className="card-flat p-6">
                    <h4 className="font-heading font-bold text-navy-900 mb-2">Mail Forwarding</h4>
                    <p className="text-slate-600 text-sm mb-0">
                      Geschäftspost wird gescannt und digital an Sie weitergeleitet. 
                      Physische Weiterleitung auf Wunsch gegen Aufpreis möglich.
                    </p>
                  </div>
                  
                  <div className="card-flat p-6">
                    <h4 className="font-heading font-bold text-navy-900 mb-2">Annual Report Filing</h4>
                    <p className="text-slate-600 text-sm mb-0">
                      Northwest erinnert Sie an fällige Jahresberichte und kann diese 
                      auch in Ihrem Namen einreichen.
                    </p>
                  </div>
                  
                  <div className="card-flat p-6">
                    <h4 className="font-heading font-bold text-navy-900 mb-2">EIN Service</h4>
                    <p className="text-slate-600 text-sm mb-0">
                      Beantragung Ihrer Employer Identification Number (Steuernummer) bei der IRS — 
                      besonders wertvoll für internationale Gründer, die dies nicht selbst online tun können.
                    </p>
                  </div>
                  
                  <div className="card-flat p-6">
                    <h4 className="font-heading font-bold text-navy-900 mb-2">Operating Agreement</h4>
                    <p className="text-slate-600 text-sm mb-0">
                      Ein angepasstes Operating Agreement für Ihre LLC ist im Gründungspaket enthalten — 
                      bei vielen Konkurrenten ein kostenpflichtiges Extra.
                    </p>
                  </div>
                </div>
              </section>

              {/* Preise */}
              <section id="preise" className="mb-16">
                <h2>Preise und Pakete</h2>
                <p>
                  Northwest hat eine erfrischend einfache Preisstruktur — keine versteckten Upsells 
                  oder verwirrenden Paketoptionen:
                </p>

                <div className="overflow-x-auto my-8">
                  <Table>
                    <TableHeader>
                      <TableRow className="table-header">
                        <TableHead>Leistung</TableHead>
                        <TableHead>Preis</TableHead>
                        <TableHead>Details</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow className="bg-teal-50 border-l-4 border-l-teal-600">
                        <TableCell className="font-medium">LLC Gründung</TableCell>
                        <TableCell className="font-bold">$39 + Staatsgebühren</TableCell>
                        <TableCell>Articles of Organization, Operating Agreement, 1 Jahr RA</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Registered Agent (ab Jahr 2)</TableCell>
                        <TableCell className="font-bold">$125/Jahr</TableCell>
                        <TableCell>Alle 50 Staaten, digitale Dokumentenzustellung</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">EIN Service</TableCell>
                        <TableCell className="font-bold">$50</TableCell>
                        <TableCell>Beantragung Ihrer Steuernummer bei der IRS</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Mail Forwarding</TableCell>
                        <TableCell className="font-bold">$125/Jahr</TableCell>
                        <TableCell>Scannen und digitale Weiterleitung</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Annual Report Filing</TableCell>
                        <TableCell className="font-bold">$100 + Gebühren</TableCell>
                        <TableCell>Einreichung im Namen Ihrer LLC</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>

                <div className="card-highlight p-6">
                  <h4 className="font-heading font-bold text-navy-900 mb-2">Rechenbeispiel Wyoming LLC:</h4>
                  <ul className="space-y-1 text-sm">
                    <li>Northwest Service Fee: $39</li>
                    <li>Wyoming State Fee: $100</li>
                    <li>Registered Agent (1. Jahr): $0 (inklusive)</li>
                    <li className="font-bold border-t pt-2 mt-2">Gesamt im 1. Jahr: $139</li>
                  </ul>
                </div>
              </section>

              {/* Praxistest */}
              <section id="praxistest" className="mb-16">
                <h2>Unser Praxistest</h2>
                <p>
                  Wir haben den kompletten Gründungsprozess einer Wyoming LLC mit Northwest durchlaufen. 
                  Hier unsere Erfahrungen Schritt für Schritt:
                </p>

                <div className="space-y-4 my-8">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-teal-600 text-white text-sm font-bold rounded-full">
                      1
                    </div>
                    <div>
                      <h4 className="font-heading font-bold text-navy-900 mb-1">Registrierung (3 Minuten)</h4>
                      <p className="text-slate-600 text-sm">
                        Die Anmeldung ist unkompliziert. Sie geben Ihren gewünschten LLC-Namen, 
                        Bundesstaat und Kontaktdaten ein. Northwest prüft sofort die Namensverfügbarkeit.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-teal-600 text-white text-sm font-bold rounded-full">
                      2
                    </div>
                    <div>
                      <h4 className="font-heading font-bold text-navy-900 mb-1">Dokumentenerstellung (5 Minuten)</h4>
                      <p className="text-slate-600 text-sm">
                        Sie beantworten einige Fragen zu Ihrer LLC (Zweck, Mitglieder, Manager). 
                        Northwest generiert automatisch die Articles of Organization und das Operating Agreement.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-teal-600 text-white text-sm font-bold rounded-full">
                      3
                    </div>
                    <div>
                      <h4 className="font-heading font-bold text-navy-900 mb-1">Bezahlung & Einreichung (2 Minuten)</h4>
                      <p className="text-slate-600 text-sm">
                        Nach der Bezahlung reicht Northwest Ihren Antrag beim Secretary of State ein — 
                        in unserem Fall am selben Werktag.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-teal-600 text-white text-sm font-bold rounded-full">
                      4
                    </div>
                    <div>
                      <h4 className="font-heading font-bold text-navy-900 mb-1">Genehmigung (1 Werktag)</h4>
                      <p className="text-slate-600 text-sm">
                        Wyoming genehmigte unsere LLC innerhalb von 24 Stunden. Wir erhielten 
                        sofort eine E-Mail mit den genehmigten Dokumenten.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-teal-600 text-white text-sm font-bold rounded-full">
                      5
                    </div>
                    <div>
                      <h4 className="font-heading font-bold text-navy-900 mb-1">Dashboard-Zugang</h4>
                      <p className="text-slate-600 text-sm">
                        Alle Dokumente sind im übersichtlichen Online-Dashboard verfügbar. 
                        Compliance-Fristen werden automatisch getrackt.
                      </p>
                    </div>
                  </div>
                </div>

                <p>
                  <strong>Gesamtzeit von Anmeldung bis fertiger LLC: Unter 48 Stunden.</strong> Das ist 
                  bemerkenswert schnell, besonders für einen internationalen Gründungsprozess.
                </p>
              </section>

              {/* Datenschutz */}
              <section id="datenschutz" className="mb-16">
                <h2>Datenschutz — Der wichtigste Unterschied</h2>
                <p>
                  Hier hebt sich Northwest deutlich von der Konkurrenz ab: <strong>Northwest verwendet 
                  ihre eigene Geschäftsadresse auf allen öffentlichen Dokumenten</strong> — nicht Ihre 
                  persönliche Adresse.
                </p>
                <p>
                  Das bedeutet: Wenn jemand die öffentlichen LLC-Unterlagen beim Secretary of State 
                  einsieht, findet er die Adresse von Northwest, nicht Ihre Privatadresse. Bei den 
                  meisten anderen Anbietern (besonders den "kostenlosen" wie Incfile oder ZenBusiness) 
                  erscheint Ihre persönliche Adresse in öffentlichen Registern.
                </p>
                
                <div className="card-highlight p-6 my-8">
                  <h4 className="font-heading font-bold text-navy-900 mb-2">Warum ist das wichtig?</h4>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
                      <span>Keine ungewollte Post oder Spam an Ihre Privatadresse</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
                      <span>Professionelleres Erscheinungsbild gegenüber Geschäftspartnern</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
                      <span>Schutz Ihrer persönlichen Daten vor Online-Recherche</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
                      <span>Besonders wichtig für internationale Gründer ohne US-Adresse</span>
                    </li>
                  </ul>
                </div>
              </section>

              {/* Kundenservice */}
              <section id="kundenservice" className="mb-16">
                <h2>Kundenservice</h2>
                <p>
                  Wir haben den Support mehrfach getestet — telefonisch, per E-Mail und Chat:
                </p>

                <div className="grid sm:grid-cols-3 gap-4 my-8">
                  <div className="card-flat p-4 text-center">
                    <p className="text-sm text-slate-500 mb-1">Telefon-Wartezeit</p>
                    <p className="font-bold text-navy-900 text-xl">Ø 4 Min.</p>
                    <p className="text-xs text-slate-500">3 Testanrufe</p>
                  </div>
                  <div className="card-flat p-4 text-center">
                    <p className="text-sm text-slate-500 mb-1">E-Mail-Antwort</p>
                    <p className="font-bold text-navy-900 text-xl">Ø 3 Std.</p>
                    <p className="text-xs text-slate-500">Während Geschäftszeiten</p>
                  </div>
                  <div className="card-flat p-4 text-center">
                    <p className="text-sm text-slate-500 mb-1">Support-Qualität</p>
                    <p className="font-bold text-navy-900 text-xl">Sehr gut</p>
                    <p className="text-xs text-slate-500">Kompetent & hilfreich</p>
                  </div>
                </div>

                <p>
                  Besonders positiv: Die Mitarbeiter sprechen <strong>kein Callcenter-Skript</strong>. 
                  Unsere Fragen wurden individuell und kompetent beantwortet. Man merkt, dass das 
                  Team in den USA sitzt und tatsächlich Ahnung von LLC-Gründungen hat.
                </p>
              </section>

              {/* Dashboard */}
              <section id="dashboard" className="mb-16">
                <h2>Dashboard & Verwaltung</h2>
                <p>
                  Das Online-Dashboard ist übersichtlich und funktional gestaltet:
                </p>
                <ul>
                  <li><strong>Dokumentenablage:</strong> Alle Gründungsdokumente, Jahresberichte und erhaltene Post sind digital archiviert</li>
                  <li><strong>Compliance-Tracker:</strong> Zeigt anstehende Fristen für Annual Reports</li>
                  <li><strong>Mail-Center:</strong> Eingegangene Post wird gescannt und hier angezeigt</li>
                  <li><strong>Rechnungen:</strong> Übersicht aller Zahlungen und Rechnungen</li>
                </ul>
                <p>
                  Was fehlt: Eine dedizierte Mobile App. Das Dashboard ist zwar mobilfreundlich, 
                  aber eine native App wäre ein Nice-to-have.
                </p>
              </section>

              {/* Pros & Cons */}
              <section id="pros-cons" className="mb-16">
                <h2>Vorteile & Nachteile</h2>
                
                <ProsConsList
                  pros={[
                    'Echter Datenschutz — Ihre Adresse bleibt privat',
                    'Fairer, transparenter Preis ohne versteckte Upsells',
                    'US-basierter, kompetenter Kundenservice',
                    'Über 25 Jahre Erfahrung und 3+ Mio. Kunden',
                    'Schnelle Bearbeitung (oft am selben Tag)',
                    'Operating Agreement im Preis inklusive',
                    '1 Jahr Registered Agent kostenlos bei Gründung',
                    'Übersichtliches Online-Dashboard'
                  ]}
                  cons={[
                    'Keine mobile App verfügbar',
                    'Nicht der absolut günstigste Anbieter (aber mit besserem Service)',
                    'Keine deutschen Sprachoptionen',
                    'Keine Buchhaltungs- oder Steuerservices'
                  ]}
                />
              </section>

              {/* Vergleich */}
              <section id="vergleich" className="mb-16">
                <h2>Northwest vs. Alternativen</h2>

                <div className="overflow-x-auto my-8">
                  <Table>
                    <TableHeader>
                      <TableRow className="table-header">
                        <TableHead>Anbieter</TableHead>
                        <TableHead>Bewertung</TableHead>
                        <TableHead>Gründungspreis</TableHead>
                        <TableHead>Registered Agent</TableHead>
                        <TableHead>Datenschutz</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {competitorComparison.map((row, index) => (
                        <TableRow 
                          key={index}
                          className={row.isWinner ? 'bg-teal-50 border-l-4 border-l-teal-600' : ''}
                        >
                          <TableCell className="font-medium">
                            {row.name}
                            {row.isWinner && (
                              <span className="ml-2 text-xs bg-teal-600 text-white px-2 py-0.5 rounded-sm">
                                Testsieger
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            <StarRating rating={row.rating} size="sm" showValue={false} />
                            <span className="ml-1 text-sm">{row.rating}/5</span>
                          </TableCell>
                          <TableCell>{row.price}</TableCell>
                          <TableCell>{row.raIncluded}</TableCell>
                          <TableCell>{row.privacy}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <p>
                  Die "$0 Gründung" von ZenBusiness und Incfile klingt verlockend, aber der Teufel 
                  steckt im Detail: Der Registered Agent Service kostet extra, Ihre Adresse wird 
                  öffentlich gemacht, und Sie werden mit Upsells bombardiert.
                </p>
              </section>

              {/* Zielgruppe */}
              <section id="zielgruppe" className="mb-16">
                <h2>Für wen eignet sich Northwest?</h2>
                
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="card-flat p-4">
                    <h4 className="font-heading font-bold text-navy-900 text-sm mb-2 flex items-center gap-2">
                      <Check className="w-4 h-4 text-teal-600" />
                      Perfekt für
                    </h4>
                    <ul className="space-y-1 text-sm text-slate-600">
                      <li>• Internationale Unternehmer</li>
                      <li>• Digitale Nomaden</li>
                      <li>• E-Commerce-Verkäufer</li>
                      <li>• Freelancer mit US-Kunden</li>
                      <li>• Datenschutz-bewusste Gründer</li>
                    </ul>
                  </div>
                  <div className="card-flat p-4">
                    <h4 className="font-heading font-bold text-navy-900 text-sm mb-2">Weniger geeignet für</h4>
                    <ul className="space-y-1 text-sm text-slate-600">
                      <li>• Reine Preisoptimierer (günstigere Alternativen existieren)</li>
                      <li>• Wer deutschen Support benötigt</li>
                      <li>• Wer All-in-One inkl. Buchhaltung sucht</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Fazit */}
              <section id="fazit" className="mb-16">
                <h2>Unser Fazit</h2>
                
                <div className="card-highlight p-8 mb-8">
                  <h3 className="font-heading text-xl font-bold text-navy-900 mb-4">
                    Gesamtbewertung: 4.9/5
                  </h3>
                  
                  <div className="grid sm:grid-cols-2 gap-4 mb-6">
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Preis-Leistung</p>
                      <StarRating rating={5} size="sm" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Benutzerfreundlichkeit</p>
                      <StarRating rating={4.5} size="sm" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Datenschutz</p>
                      <StarRating rating={5} size="sm" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Kundenservice</p>
                      <StarRating rating={4.5} size="sm" />
                    </div>
                  </div>

                  <p className="text-slate-700 mb-6">
                    Northwest Registered Agent ist unser klarer Testsieger für 2026. Die Kombination aus 
                    echtem Datenschutz, fairem Preis, kompetenten Support und über 25 Jahren Erfahrung macht 
                    sie zur besten Wahl für internationale Unternehmer, die eine US-LLC gründen möchten.
                  </p>

                  <a
                    href="https://www.northwestregisteredagent.com"
                    target="_blank"
                    rel="nofollow sponsored noopener noreferrer"
                    className="cta-primary"
                    data-testid="fazit-cta"
                  >
                    Northwest Registered Agent — Jetzt LLC gründen ab $39
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </a>
                </div>
              </section>

              {/* FAQ */}
              <section className="mb-16">
                <h2>Häufig gestellte Fragen</h2>
                
                <Accordion type="single" collapsible className="w-full">
                  {faqs.map((faq, index) => (
                    <AccordionItem key={index} value={`faq-${index}`}>
                      <AccordionTrigger className="text-left font-heading font-semibold text-navy-900">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-slate-600">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </section>

            </article>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-4">
            <div className="space-y-8">
              <TableOfContents items={tocItems} />
              
              <CTABox
                variant="sidebar"
                highlights={[
                  'Ab $39 + Staatsgebühren',
                  '1 Jahr Registered Agent kostenlos',
                  'Echter Datenschutz inklusive',
                  'US-basierter Support'
                ]}
              />

              <div className="bg-slate-50 border border-slate-200 rounded-sm p-6">
                <h4 className="font-heading font-bold text-navy-900 mb-4">Weitere Testberichte</h4>
                <ul className="space-y-3">
                  <li>
                    <Link to="/registered-agent" className="text-sm text-slate-700 hover:text-teal-600">
                      → Registered Agent Vergleich 2026
                    </Link>
                  </li>
                  <li>
                    <Link to="/ratgeber/beste-llc-gruendungsservices-vergleich" className="text-sm text-slate-700 hover:text-teal-600">
                      → Die 5 besten LLC-Services
                    </Link>
                  </li>
                  <li>
                    <Link to="/llc-usa-gruenden" className="text-sm text-slate-700 hover:text-teal-600">
                      → LLC Gründung USA Guide
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default NorthwestReviewPage;
